// Distinção PROVEDOR-FORA × FONTE-RUIM no caminho de sign do ao vivo, e a
// política de back-off que substitui a tempestade de failover.
//
// CONTEXTO (fix/tv-provider-unavailable-no-storm): "todos os canais
// indisponíveis" era o Cloudflare do provedor rate-limitando o IP do backend,
// AMPLIFICADO pela nossa tempestade de failover — cada tap num canal disparava
// ~15 tentativas de sign em ~2,5s (`tryAutoSwitch('sign')` rotaciona fonte por
// fonte), floodando o edge do provedor e realimentando o 429.
//
// O backend agora distingue (docs/specs/api.md §7.4.0):
//   - 503 { error:'provider_unavailable', reason, retryAfter } (+ header
//     Retry-After) → o PROVEDOR está fora/rate-limitado. Rotacionar NÃO adianta
//     (todas as fontes vão falhar) e é justamente o que floda. Back-off por
//     retryAfter e re-tenta a MESMA fonte, com teto.
//   - 400 { error:'sign_failed' } → fonte específica ruim/requisição inválida.
//     Rotacionar segue legítimo (é outra fonte, não floda).
//
// Estas são funções PURAS (testáveis sem React): o LivePlayerProvider só
// EXECUTA a decisão. Toda a política de "provedor fora" mora aqui, num só lugar.

import { ApiError } from '../api'

export const PROVIDER_UNAVAILABLE_CODE = 'provider_unavailable'

// Teto de re-tentativas com back-off por TAP antes de desistir com a mensagem
// calma. Objetivo do fix: de ~15 POSTs de sign/tap pra ~1 + poucas re-tentativas
// ESPAÇADAS (respeitando retryAfter), em vez de 15 em ~2,5s.
export const MAX_PROVIDER_UNAVAILABLE_RETRIES = 3
// Piso do back-off: mesmo que o backend mande retryAfter:0/1, nunca re-tenta
// mais rápido que isso (protege o edge que já está rate-limitado).
export const MIN_BACKOFF_MS = 3_000
// Teto do back-off: não deixa o usuário num limbo "tentando" por minutos.
export const MAX_BACKOFF_MS = 60_000
// Fallback quando a resposta não trouxe retryAfter (backend antigo / sem header).
export const DEFAULT_BACKOFF_MS = 10_000
// Jitter máximo somado ao back-off. Espalha a re-tentativa entre DEVICES da
// frota (todos recebem o mesmo retryAfter do circuito global do backend) pra o
// rebanho não bater no provedor no mesmo instante quando a janela reabre.
export const BACKOFF_JITTER_MS = 2_000

/**
 * True se o erro é o sinal de PROVEDOR-FORA do backend (503 provider_unavailable).
 * SÓ isso desliga a rotação de fontes — qualquer outra falha (400 sign_failed,
 * erro de load, rede) continua rotacionando (comportamento antigo, legítimo).
 */
export function isProviderUnavailable(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 503 &&
    err.code === PROVIDER_UNAVAILABLE_CODE
  )
}

/** retryAfter (segundos) que o backend pediu, ou null se ausente/inválido. */
export function providerRetryAfterSec(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null
  const v = err.retryAfter
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null
}

export type SignFailureDecision =
  // fonte ruim (ou erro genérico): rotaciona pra próxima fonte — comportamento antigo.
  | { type: 'rotate' }
  // provedor-fora: espera `delayMs` e re-tenta a MESMA fonte (`attempt` = nº da re-tentativa).
  | { type: 'backoff'; delayMs: number; attempt: number }
  // provedor-fora + estourou o teto: mensagem calma "indisponível, tente mais tarde".
  | { type: 'giveup' }

export interface SignFailureInput {
  err: unknown
  /** Quantas re-tentativas de back-off já foram feitas neste tap (0 na 1ª falha). */
  retries: number
  maxRetries?: number
  /** Injetável pra teste determinístico. Default Math.random. */
  rand?: () => number
}

function clampBackoffMs(retryAfterSec: number | null): number {
  if (retryAfterSec == null) return DEFAULT_BACKOFF_MS
  const ms = retryAfterSec * 1000
  return Math.min(MAX_BACKOFF_MS, Math.max(MIN_BACKOFF_MS, ms))
}

/**
 * Decide o que fazer quando o sign do ao vivo falha.
 *  - Provedor-fora (503 provider_unavailable): back-off por retryAfter (clamp +
 *    jitter) e re-tenta a MESMA fonte, até o teto → depois `giveup`. NUNCA rotaciona.
 *  - Qualquer outra falha: `rotate` (o failover de fontes de sempre).
 */
export function decideSignFailure(input: SignFailureInput): SignFailureDecision {
  const maxRetries = input.maxRetries ?? MAX_PROVIDER_UNAVAILABLE_RETRIES
  if (!isProviderUnavailable(input.err)) return { type: 'rotate' }
  if (input.retries >= maxRetries) return { type: 'giveup' }
  const rand = input.rand ?? Math.random
  const base = clampBackoffMs(providerRetryAfterSec(input.err))
  const jitter = Math.floor(rand() * BACKOFF_JITTER_MS)
  return { type: 'backoff', delayMs: base + jitter, attempt: input.retries + 1 }
}
